import { theme, Divider, Typography } from "antd";
import {
  ArrowUpOutlined,
  ArrowDownOutlined,
  ArrowRightOutlined,
} from "@ant-design/icons";
import { getChangeDirection, shuffledColors } from "../cardUtils";
import { MetricDisplay } from "./MetricDisplay";
import { getChangeIcon } from "../cardUtils";

// Shared variants definition
export const metricComparisonVariants = [
  "default",
  "card",
  "vertical",
  "horizontalCard",
  "softCardRow",
  "highlight",
  "dashboard",
  "minimal",
] as const;

export type MetricComparisonVariant = (typeof metricComparisonVariants)[number];

export interface MetricComparisonProps {
  previousValue: number;
  currentValue: number;
  unit?: string;
  changeValue?: number;
  className?: string;
  formatter?: (value: number) => string;
  precision?: number;
  variant?: MetricComparisonVariant;
  background?: boolean;
  label?: string;
}

const CardVariant = ({
  previousValue,
  currentValue,
  changeValue,
  unit,
  label,
  className,
}: MetricComparisonProps) => {
  const { token } = theme.useToken();
  const percent = changeValue ? Math.abs(changeValue).toFixed(2) : "0.00";
  const colors = shuffledColors(currentValue);
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "var(--padding-2)",
        width: "100%",
      }}
      className={className}
    >
      {label && (
        <Typography.Text className="vzbl-text-3">{label}</Typography.Text>
      )}
      <div style={{ display: "flex", gap: 8, width: "100%" }}>
        {/* Percent Change Block */}
        <MetricDisplay
          label="Percent Change"
          value={percent}
          unit="%"
          variant="card"
          changeType={getChangeDirection(changeValue)}
          style={{ flex: 1 }}
          color={colors[0]}
        />
        {/* Previous Value Block */}
        <MetricDisplay
          label="Previous"
          value={previousValue}
          unit={unit}
          variant="card"
          style={{ flex: 1, position: "relative" }}
          extra={
            <span
              style={{
                position: "absolute",
                right: -32,
                top: "50%",
                transform: "translateY(-50%)",
                background: "var(--color-foreground-quaternary)",
                borderRadius: "50%",
                width: 40,
                height: 40,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                border: `1px solid var(--color-foreground-quaternary)`,
                zIndex: 1,
              }}
            >
              <ArrowRightOutlined
                style={{
                  fontSize: 24,
                  color: "var(--color-foreground-quaternary)",
                }}
              />
            </span>
          }
          color={colors[1]}
        />
        {/* Current Value Block */}
        <MetricDisplay
          label="Current"
          value={currentValue}
          unit={unit}
          variant="card"
          style={{ flex: 1 }}
          color={colors[2]}
        />
      </div>
    </div>
  );
};

const VerticalVariant = ({
  previousValue,
  currentValue,
  changeValue,
  unit,
  label,
  className,
}: MetricComparisonProps) => {
  const percent = changeValue ? Math.abs(changeValue).toFixed(2) : "0.00";
  return (
    <div
      style={{ display: "flex", flexDirection: "column", gap: 2 }}
      className={className}
    >
      {label && (
        <Typography.Text
          className="vzbl-text-3"
          style={{ marginBottom: "var(--padding-2)" }}
        >
          {label}
        </Typography.Text>
      )}
      <span>
        <b>Previous:</b>{" "}
        <MetricDisplay value={previousValue} unit={unit} variant="minimal" />
      </span>
      <span>
        <b>Current:</b>{" "}
        <MetricDisplay value={currentValue} unit={unit} variant="minimal" />
      </span>
      <span>
        <b>Change:</b>{" "}
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "var(--padding-1)",
          }}
        >
          {getChangeIcon(getChangeDirection(changeValue))}
          <span>{percent}%</span>
        </div>
      </span>
    </div>
  );
};

const HorizontalCardVariant = ({
  previousValue,
  currentValue,
  changeValue,
  unit,
  label,
  className,
}: MetricComparisonProps) => {
  const { token } = theme.useToken();
  const percent = changeValue ? Math.abs(changeValue).toFixed(2) : "0.00";
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "var(--padding-2)",
      }}
      className={className}
    >
      {label && (
        <Typography.Text className="vzbl-text-3">{label}</Typography.Text>
      )}
      <div style={{ display: "flex", alignItems: "center", gap: 24 }}>
        <MetricDisplay
          label="Overall Change"
          value={percent}
          unit="%"
          variant="card"
          changeType={getChangeDirection(changeValue)}
          style={{
            flex: 1,
            minWidth: 0,
            background: "var(--color-background-tertiary)",
          }}
        />
        <MetricDisplay
          label="PREVIOUS"
          value={previousValue}
          unit={unit}
          variant="card"
          style={{
            flex: 1,
            minWidth: 0,
            position: "relative",
            background: "var(--color-background-tertiary)",
          }}
          extra={
            <span
              style={{
                position: "absolute",
                right: -24,
                top: "50%",
                transform: "translateY(-50%)",
                background: "transparent",
                borderRadius: 16,
                width: 32,
                height: 32,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                border: `1px solid var(--color-blue-1)`,
              }}
            >
              <ArrowRightOutlined
                style={{
                  fontSize: "var(--font-size-4)",
                  color: "var(--color-text-3)",
                }}
              />
            </span>
          }
        />
        <MetricDisplay
          label="CURRENT"
          value={currentValue}
          unit={unit}
          variant="card"
          style={{
            flex: 1,
            minWidth: 0,
            background: "var(--color-background-tertiary)",
          }}
        />
      </div>
    </div>
  );
};

const SoftCardRowVariant = ({
  previousValue,
  currentValue,
  changeValue,
  unit,
  label,
  className,
}: MetricComparisonProps) => {
  const percent = changeValue ? Math.abs(changeValue).toFixed(2) : "0.00";

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "var(--padding-2)",
      }}
      className={className}
    >
      {label && (
        <Typography.Text className="vzbl-text-3">{label}</Typography.Text>
      )}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 24,
          background: "var(--color-background-quaternary)",
          borderRadius: "var(--border-radius-3)",
        }}
      >
        <MetricDisplay
          label="Change"
          value={percent}
          unit="%"
          variant="card"
          changeType={getChangeDirection(changeValue)}
          style={{
            flex: 1,
            minWidth: 0,
            background: "transparent",
            border: "none",
            boxShadow: "none",
          }}
        />
        <MetricDisplay
          label="Previous"
          value={previousValue}
          unit={unit}
          variant="card"
          style={{
            flex: 1,
            minWidth: 0,
            position: "relative",
            background: "transparent",
            border: "none",
            boxShadow: "none",
          }}
          extra={
            <span
              style={{
                position: "absolute",
                right: -24,
                top: "50%",
                transform: "translateY(-50%)",
                background: "transparent",
                borderRadius: "var(--border-radius-3)",
                width: 32,
                height: 32,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
                border: `1px solid var(--color-blue-1)`,
              }}
            >
              <ArrowRightOutlined
                style={{
                  fontSize: "var(--font-size-4)",
                  color: "var(--color-text-3)",
                  background: "var(--color-background-quaternary)",
                }}
              />
            </span>
          }
        />
        <MetricDisplay
          label="Current"
          value={currentValue}
          unit={unit}
          variant="card"
          style={{
            flex: 1,
            minWidth: 0,
            background: "transparent",
            border: "none",
            boxShadow: "none",
          }}
        />
      </div>
    </div>
  );
};

const CompactVariant = ({
  previousValue,
  currentValue,
  changeValue,
  unit,
  label,
  className,
}: MetricComparisonProps) => {
  const percent = changeValue ? Math.abs(changeValue).toFixed(2) : "0.00";
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "var(--padding-2)",
        padding: "var(--padding-2)",
        background: "var(--color-background-quaternary)",
        borderRadius: "var(--border-radius-2)",
      }}
      className={className}
    >
      {label && (
        <Typography.Text
          className="vzbl-text-3"
          style={{
            padding: "var(--padding-1) var(--padding-2)",
            background: "var(--color-background-secondary)",
            borderRadius: "var(--border-radius-2)",
          }}
        >
          {label}
        </Typography.Text>
      )}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "var(--padding-2)",
          padding: "var(--padding-2)",
          background: "var(--color-background-secondary)",
          borderRadius: "var(--border-radius-2)",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "var(--padding-2)",
          }}
        >
          <MetricDisplay
            value={previousValue}
            unit={unit}
            variant="minimal"
            style={{ fontSize: "var(--font-size-3)" }}
          />
          <ArrowRightOutlined style={{ color: "var(--color-text-3)" }} />
          <MetricDisplay
            value={currentValue}
            unit={unit}
            variant="minimal"
            style={{ fontSize: "var(--font-size-3)" }}
          />
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "var(--padding-1)",
            padding: "var(--padding-1) var(--padding-2)",
            background: "var(--color-background-quaternary)",
            borderRadius: "var(--border-radius-2)",
          }}
        >
          {getChangeIcon(getChangeDirection(changeValue))}
          <Typography.Text className="vzbl-text-3">{percent}%</Typography.Text>
        </div>
      </div>
    </div>
  );
};

const HighlightVariant = ({
  previousValue,
  currentValue,
  changeValue,
  unit,
  label,
  className,
}: MetricComparisonProps) => {
  const percent = changeValue ? Math.abs(changeValue).toFixed(2) : "0.00";
  const isPositive = changeValue !== undefined && changeValue > 0;
  const color = isPositive ? "var(--color-success)" : "var(--color-error)";

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "var(--padding-3)",
        padding: "var(--padding-3)",
        background: "var(--color-background-quaternary)",
        borderRadius: "var(--border-radius-3)",
        border: `1px solid ${color}`,
      }}
      className={className}
    >
      {label && (
        <Typography.Text className="vzbl-text-2">{label}</Typography.Text>
      )}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "var(--padding-1)",
          }}
        >
          <Typography.Text className="vzbl-text-3">Previous</Typography.Text>
          <MetricDisplay
            value={previousValue}
            unit={unit}
            variant="minimal"
            style={{ fontSize: "var(--font-size-3)" }}
          />
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "var(--padding-2)",
          }}
        >
          {getChangeIcon(getChangeDirection(changeValue))}
          <Typography.Text style={{ color }}>{percent}%</Typography.Text>
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "var(--padding-1)",
          }}
        >
          <Typography.Text className="vzbl-text-3">Current</Typography.Text>
          <MetricDisplay
            value={currentValue}
            unit={unit}
            variant="minimal"
            style={{ fontSize: "var(--font-size-3)" }}
          />
        </div>
      </div>
    </div>
  );
};

const DashboardVariant = ({
  previousValue,
  currentValue,
  changeValue,
  unit,
  label,
  className,
}: MetricComparisonProps) => {
  const percent = changeValue ? Math.abs(changeValue).toFixed(2) : "0.00";
  const isPositive = changeValue !== undefined && changeValue > 0;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "var(--padding-3)",
        padding: "var(--padding-3)",
        background: "var(--color-background-quaternary)",
        borderRadius: "var(--border-radius-3)",
      }}
      className={className}
    >
      {label && (
        <Typography.Text className="vzbl-text-2">{label}</Typography.Text>
      )}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          gap: "var(--padding-3)",
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "var(--padding-2)",
            padding: "var(--padding-3)",
            background: "var(--color-background-secondary)",
            borderRadius: "var(--border-radius-2)",
          }}
        >
          <Typography.Text className="vzbl-text-3">Previous</Typography.Text>
          <MetricDisplay
            value={previousValue}
            unit={unit}
            variant="minimal"
            style={{ fontSize: "var(--font-size-4)" }}
          />
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "var(--padding-2)",
            padding: "var(--padding-3)",
            background: "var(--color-background-secondary)",
            borderRadius: "var(--border-radius-2)",
          }}
        >
          <Typography.Text className="vzbl-text-3">Current</Typography.Text>
          <MetricDisplay
            value={currentValue}
            unit={unit}
            variant="minimal"
            style={{ fontSize: "var(--font-size-4)" }}
          />
        </div>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "var(--padding-2)",
            padding: "var(--padding-3)",
            background: isPositive
              ? "var(--color-success-light)"
              : "var(--color-error-light)",
            borderRadius: "var(--border-radius-2)",
          }}
        >
          <Typography.Text className="vzbl-text-3">Change</Typography.Text>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "var(--padding-2)",
            }}
          >
            {getChangeIcon(getChangeDirection(changeValue))}
            <Typography.Text
              style={{
                fontSize: "var(--font-size-4)",
                color: isPositive
                  ? "var(--color-success)"
                  : "var(--color-error)",
              }}
            >
              {percent}%
            </Typography.Text>
          </div>
        </div>
      </div>
    </div>
  );
};

const MinimalVariant = ({
  previousValue,
  currentValue,
  changeValue,
  unit,
  className,
  formatter,
  precision = 1,
  label,
}: MetricComparisonProps) => {
  const percent = changeValue
    ? (changeValue > 0 ? "+" : "") + Math.abs(changeValue).toFixed(0) + "%"
    : "0%";
  return (
    <div
      className={className}
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        width: "100%",
        gap: 32,
        padding: 0,
        background: "none",
      }}
    >
      {label && (
        <Typography.Text className="vzbl-text-2">{label}</Typography.Text>
      )}
      {/* Current */}
      <div style={{ textAlign: "left", flex: 1 }}>
        <div className="vzbl-text-3" style={{ marginBottom: 2 }}>
          Current
        </div>
        <div className="vzbl-text-6" style={{ color: "var(--color-text-1)" }}>
          {formatter ? formatter(currentValue) : currentValue}{" "}
          {unit && <span className="vzbl-text-3">{unit}</span>}
        </div>
      </div>
      {/* Previous */}
      <div style={{ textAlign: "left", flex: 1 }}>
        <div className="vzbl-text-3" style={{ marginBottom: 2 }}>
          Previous
        </div>
        <div className="vzbl-text-6" style={{ color: "var(--color-text-1)" }}>
          {formatter ? formatter(previousValue) : previousValue}{" "}
          {unit && (
            <span
              className="vzbl-text-3"
              style={{ color: "var(--color-text-3)" }}
            >
              {unit}
            </span>
          )}
        </div>
      </div>
      {/* Change */}
      <div style={{ textAlign: "center", flex: 1 }}>
        <div className="vzbl-text-3" style={{ marginBottom: 2 }}>
          Change
        </div>
        <div
          className="vzbl-text-2"
          style={{
            color:
              changeValue && changeValue > 0
                ? "var(--color-success)"
                : changeValue && changeValue < 0
                ? "var(--color-error)"
                : "var(--color-text-1)",
          }}
        >
          {percent}
        </div>
      </div>
    </div>
  );
};

export function MetricComparison({
  previousValue,
  currentValue,
  unit,
  changeValue,
  variant = "default",
  background = false,
  label,
  className,
  formatter,
  precision,
}: MetricComparisonProps) {
  const { token } = theme.useToken();

  const commonProps = {
    previousValue,
    currentValue,
    unit,
    changeValue,
    label,
    className,
    formatter,
    precision,
  };

  switch (variant) {
    case "highlight": {
      return <HighlightVariant {...commonProps} />;
    }
    case "dashboard": {
      return <DashboardVariant {...commonProps} />;
    }
    case "horizontalCard": {
      return <HorizontalCardVariant {...commonProps} />;
    }
    case "softCardRow": {
      return <SoftCardRowVariant {...commonProps} />;
    }
    case "card": {
      return <CardVariant {...commonProps} />;
    }
    case "vertical": {
      return <VerticalVariant {...commonProps} />;
    }
    case "minimal": {
      return <MinimalVariant {...commonProps} />;
    }
    default: {
      return <CompactVariant {...commonProps} />;
    }
  }
}
